/**
 * Single source of truth for resolving a suggestion's content references to
 * human-readable titles (and best-effort content types), keyed by the content
 * id as a string.
 *
 * Resolution sources, merged lowest-to-highest priority so the resolved title
 * stays consistent with what the card summary shows (`SuggestionSummary` via
 * `parseSuggestionTitle`):
 *
 * 1. `seed` — externally resolved details (e.g. the AGG content lookup performed
 *    by `useFetchSpaceSuggestions`). Fallback for ids not mentioned in the title.
 * 2. `titleReferences` — structured id→{title,contentType} map, covering ids that
 *    may not appear literally in the title text. Keyed here by BOTH the record key
 *    and the value's `contentId` so either backend keying resolves.
 * 3. Inline title link-segments — the `{contentId:…,title:…}` object literals and
 *    resolved bare `{id}` refs parsed out of the suggestion title. These encode
 *    the exact per-reference choice the card renders, so they win.
 *
 * Content ids are numeric. The map is keyed by the id's string form — inherent
 * because JS object keys are strings and the resolution sources (parsed title
 * segments, `titleReferences`, and the AGG lookup) all supply string ids — while
 * numeric lookups coerce to the same key automatically.
 */

import type {
	ContentDetailsById,
	RawAction,
	ResolvedContent,
	TitleReferences,
} from '../__types__/SpaceSuggestion.ts';
import { parseSuggestionTitle } from './parseSuggestionTitle.ts';

export function buildContentDetails(
	title: string,
	titleReferences?: TitleReferences,
	seed?: ContentDetailsById,
): ContentDetailsById {
	const details: ContentDetailsById = { ...seed };

	if (titleReferences) {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		for (const key in titleReferences) {
			const ref = titleReferences[key];
			if (!ref || !ref.title) {
				continue;
			}
			const resolved: ResolvedContent = {
				title: ref.title,
				contentType: ref.contentType || undefined,
			};
			// Index by the record key and the value's contentId so the action's
			// contentId resolves regardless of which the backend keys by.
			details[key] = resolved;
			if (ref.contentId) {
				details[ref.contentId] = resolved;
			}
		}
	}

	for (const segment of parseSuggestionTitle(title, titleReferences)) {
		if (segment.type === 'link') {
			details[segment.contentId] = {
				title: segment.title,
				contentType: segment.contentType,
			};
		}
	}

	return details;
}

/**
 * Resolves a content id to its display title, falling back to the bare id as a
 * last resort so a missing resolution degrades to the id rather than an empty
 * string.
 */
export function resolveContentName(
	contentId: number,
	details: ContentDetailsById,
): string | number {
	return details[contentId]?.title ?? contentId;
}

/**
 * Resolves the content type used to pick a row/link icon, or `undefined` when
 * unresolved so callers can apply their own fallback icon.
 */
export function resolveContentType(
	contentId: number,
	details: ContentDetailsById,
): string | undefined {
	return details[contentId]?.contentType;
}

/**
 * Builds a map from a CREATE_FOLDER `alias` to its folder title within a single
 * suggestion. Used to resolve a MOVE action's `destinationAlias` to a
 * human-readable folder name created earlier in the same suggestion.
 */
export function buildAliasToTitleMap(actions: RawAction[]): Record<string, string> {
	const map: Record<string, string> = {};
	for (const action of actions) {
		if (action.actionType === 'CREATE_FOLDER') {
			map[action.parameters.alias] = action.parameters.title;
		}
	}
	return map;
}
